import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Typography, Paper, Box, Tabs, Tab, Button, Chip, Stack, IconButton, InputAdornment, TextField } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { color } from '../theme/tokens';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import SEO from './common/SEO';
import './Detail.css';
import config from '../config';
import Research, { StudyStrengthIndicator } from './Research';
import ArticleDetail from './Article';
import { searchProductsForSupplement } from './products/amazonApi';
import ProductCard from './products/ProductCard';
import { fetchConditions, fetchSupplements } from '../Utils';
import { Helmet } from 'react-helmet-async';
import { logAnalyticsEvent } from '../utils/analytics';
import usePageReadTracking from '../hooks/usePageReadTracking';
import { ScienceIcon, ShoppingCartIcon, CheckCircleIcon, BalanceIcon, ShieldIcon, LocalPharmacyIcon, LocalFloristIcon, HealthAndSafetyIcon, HealingIcon } from '../icons';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BiotechIcon from '@mui/icons-material/Biotech';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

const getPlainText = (content) => {
  if (!content) return '';
  const value = Array.isArray(content) ? content.join(' ') : String(content);
  return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

const getFirstSentence = (content, fallback = '') => {
  const text = getPlainText(content);
  if (!text) return fallback;
  const match = text.match(/^(.+?[.!?])\s/);
  return match ? match[1] : `${text.slice(0, 150)}${text.length > 150 ? '...' : ''}`;
};

const formatGoalLabel = (goalSlug) => config.PRESET_HEALTH_GOALS?.[goalSlug] || goalSlug
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, char => char.toUpperCase());

const normalizeSupplementKey = (value = '') => String(value)
  .trim()
  .toLowerCase()
  .replace(/[\s-]+/g, '_');

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());

const getReferrerDomain = () => {
  if (!document.referrer) return '';
  try {
    return new URL(document.referrer).hostname;
  } catch (_) {
    return '';
  }
};


function Detail({ dataKey, detailKey, onLoadingComplete }) {
  const { supplementKey, conditionKey, articleId } = useParams();
  const theme = useTheme();
  const itemKey = dataKey === 'supplements' ? supplementKey : conditionKey;
  const [selectedItem, setSelectedItem] = useState(null);
  const [supplements, setSupplements] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [itemName, setItemName] = useState('');
  const [productCount, setProductCount] = useState(null);
  const [productPreview, setProductPreview] = useState([]);
  const [stackGoalSlugs, setStackGoalSlugs] = useState([]);
  const [stackGoalsLoaded, setStackGoalsLoaded] = useState(false);
  const [relatedSupplements, setRelatedSupplements] = useState([]);
  const [researchPreview, setResearchPreview] = useState([]);
  const [researchTotal, setResearchTotal] = useState(null);
  const [researchPreviewLoading, setResearchPreviewLoading] = useState(false);
  const linkedItems = useRef(new Set());
  const scrollPosition = useRef(0);
  const ctaViewedKey = useRef(null);
  const readContentRef = useRef(null);
  const showAffiliateStore = config.FEATURES.AFFILIATE_STORE;
  const showResearchTabConditions = config.FEATURES.SHOW_RESEARCH_TAB_CONDITIONS;
  useEffect(() => {
    const loadProducts = async () => {
      if (!showAffiliateStore || dataKey !== 'supplements') return;
      try {
        const key = itemKey;
        if (!key) return;
        const products = await searchProductsForSupplement(key);
        setProductCount(products?.length || 0);
        setProductPreview((products || []).slice(0, 3));
      } catch (e) {
        setProductCount(0);
        setProductPreview([]);
      }
    };
    loadProducts();
  }, [itemKey, dataKey, showAffiliateStore]);

  useEffect(() => {
    const loadSupplementGoals = async () => {
      if (dataKey !== 'supplements' || !itemKey) return;
      setStackGoalsLoaded(false);
      try {
        const response = await fetch(`${config.BASE_URL}/supplement-goals/${encodeURIComponent(itemKey)}`);
        if (!response.ok) {
          setStackGoalSlugs([]);
          return;
        }
        const data = await response.json();
        const uniqueGoals = Array.from(new Set((data.goals || []).filter(Boolean)));
        uniqueGoals.sort();
        setStackGoalSlugs(uniqueGoals.slice(0, 5));
      } catch (error) {
        setStackGoalSlugs([]);
      } finally {
        setStackGoalsLoaded(true);
      }
    };

    loadSupplementGoals();
  }, [dataKey, itemKey]);

  useEffect(() => {
    if (dataKey === 'supplements') {
      if (!stackGoalsLoaded) return;
      if (ctaViewedKey.current === itemKey) return;
      ctaViewedKey.current = itemKey;
      logAnalyticsEvent('supplement_detail_cta_viewed', {
        supplement_key: itemKey,
        chip_count: stackGoalSlugs.length,
        has_goal_chips: stackGoalSlugs.length > 0,
        referrer_domain: getReferrerDomain(),
      });
    } else if (dataKey === 'conditions' && itemKey && itemName) {
      if (ctaViewedKey.current === itemKey) return;
      ctaViewedKey.current = itemKey;
      logAnalyticsEvent('condition_detail_cta_viewed', {
        condition_key: itemKey,
        condition_name: itemName,
        referrer_domain: getReferrerDomain(),
      });
    }
  }, [dataKey, itemKey, itemName, stackGoalsLoaded, stackGoalSlugs.length]);

  useEffect(() => {
    const loadResearchPreview = async () => {
      if (dataKey !== 'supplements' || !itemKey) return;
      setResearchPreviewLoading(true);
      try {
        const queryParams = new URLSearchParams({
          mode: 'list',
          page: '1',
          // Keep the preview request small to avoid exposing a larger scrapeable article batch.
          pageSize: '3',
          sortBy: 'study_strength',
          supplementKey: itemKey,
        });
        const response = await fetch(`${config.EDGE_FUNCTION_URL}/getResearchArticles?${queryParams}`, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          setResearchPreview([]);
          setResearchTotal(null);
          return;
        }
        const payload = await response.json();
        const articles = payload.data || [];
        // Re-sort by final score since the edge function merges pdf_study_strength
        // post-query, which can change scores after the DB-level sort.
        articles.sort((a, b) => (b.ai_study_strength?.score || 0) - (a.ai_study_strength?.score || 0));
        setResearchPreview(articles.slice(0, 3));
        setResearchTotal(payload.pagination?.totalItems ?? articles.length);
      } catch (error) {
        setResearchPreview([]);
        setResearchTotal(null);
      } finally {
        setResearchPreviewLoading(false);
      }
    };

    loadResearchPreview();
  }, [dataKey, itemKey]);

  const navigate = useNavigate();
  const location = useLocation();

  // Engagement tracking (STACK-479). A `/research` path means the reader asked
  // for something other than this page — the Research tab, or a nested article
  // rendered over it — so the detail page is not what is being read and must
  // not accrue dwell. Same signal the canonical link below keys off.
  usePageReadTracking({
    surface: dataKey === 'conditions' ? 'condition_detail' : 'supplement_detail',
    contentId: itemKey,
    contentTitle: itemName,
    contentRef: readContentRef,
    enabled: !!selectedItem && !!itemName && !location.pathname.includes('/research'),
  });

  // Function to generate meta description from overview
  const generateDescription = React.useCallback(() => {
    if (!selectedItem?.cards) return '';

    // Find the overview card
    const overviewCard = selectedItem.cards.find(
      card => card[detailKey]?.toLowerCase() === 'overview'
    );

    if (overviewCard?.content) {
      // Clean the content (remove HTML tags)
      const cleanContent = overviewCard.content.replace(/<[^>]*>/g, '');
      
      // Get first 155 characters, try to end at a complete sentence
      let description = cleanContent.substring(0, 155);
      const lastPeriod = description.lastIndexOf('.');
      
      if (lastPeriod > 120) { // Only trim if we have a decent length
        description = description.substring(0, lastPeriod + 1);
      } else {
        // Add ellipsis if we truncated mid-sentence
        description = description.trim() + '...';
      }
      
      return description;
    }

    // Fallback description if no overview found
    return `Learn about ${itemName} ${dataKey === 'supplements' ? 
      'supplement benefits, dosage, and side effects' : 
      'symptoms, treatments, and related conditions'}`;
  }, [selectedItem, itemName, dataKey, detailKey]);

  // Function to generate structured data (JSON-LD)
  const generateStructuredData = React.useCallback(() => {
    if (!selectedItem || !itemName) return null;

    const pageUrl = `https://my-stack.ai/${dataKey}/${itemKey}`;
    const description = generateDescription(); // Reuse the meta description logic

    let schema = {
      "@context": "https://schema.org",
      "@type": "", // Will be set below
      "name": itemName,
      "description": description,
      "url": pageUrl,
      "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
      }
    };

    if (dataKey === 'supplements') {
        schema["@type"] = "DietarySupplement";
    } else if (dataKey === 'conditions') {
        schema["@type"] = "MedicalCondition";
        // Find the symptoms card to add to structured data
        const symptomsCard = selectedItem.cards.find(card => card.title?.toLowerCase() === 'symptoms');
        if (symptomsCard && Array.isArray(symptomsCard.content)) {
            schema.signOrSymptom = symptomsCard.content.map(symptom => ({
                "@type": "MedicalSymptom",
                "name": symptom.replace(/<[^>]*>/g, '') // Basic cleaning
            }));
        }
    }
    return schema;
  }, [selectedItem, itemName, dataKey, itemKey, detailKey, generateDescription]);

  const seoTitle = `${itemName} - ${dataKey === 'supplements' ? 'Benefits, Dosage & Research' : 'Symptoms & Treatments'} | myStack`;
  const seoDesc = React.useMemo(() => generateDescription(), [generateDescription]);
  const structuredData = React.useMemo(() => generateStructuredData(), [generateStructuredData]);

  const getActiveTab = () => {
    if (location.pathname.includes('/research')) return 1;
    if (location.pathname.includes('/products')) {
      setTimeout(() => navigate(`/${dataKey}/${itemKey}`, { replace: true }), 0);
      return 0;
    }
    return 0;
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  // Log view_item event when item data is loaded
  useEffect(() => {
    if (selectedItem && itemName && itemKey && dataKey) {
      logAnalyticsEvent('view_item', {
        item_id: itemKey,
        item_name: itemName,
        item_category: dataKey // 'supplements' or 'conditions'
      });
    }
  }, [selectedItem, itemName, itemKey, dataKey]);

  useEffect(() => {
  }, [activeTab]);

  useEffect(() => {
    if (itemKey) {
      fetchData();
      loadSupplements();
      loadConditions();
    }
  }, [itemKey, onLoadingComplete, dataKey]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${config.FIREBASE_STORAGE_BASE_URL}${dataKey}%2F${itemKey}.json?alt=media`);
      setSelectedItem(response.data);
      if (onLoadingComplete) {
        onLoadingComplete();
      }
    } catch (error) {
    }
  };

  const loadSupplements = async () => {
    const supplementsList = await fetchSupplements(config);
    setSupplements(supplementsList);

    if (dataKey === 'supplements') {
      const foundItem = supplementsList.find(supplement => supplement.key === itemKey);
      if (foundItem) {
        setItemName(foundItem.name);
      }
    }
  };

  const loadConditions = async () => {
    const conditionsList = await fetchConditions(config);
    setConditions(conditionsList);

    if (dataKey === 'conditions') {
      const foundItem = conditionsList.find(condition => condition.key === itemKey);
      if (foundItem) {
        setItemName(foundItem.name);
      }
    }
  };

  // Function to switch between tabs and navigate to routes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Log tab view event before navigating
    let eventName = '';
    if (newValue === 0) eventName = 'click_details_tab';
    else if (newValue === 1) eventName = 'click_research_tab';
    else if (newValue === 2 && dataKey === 'supplements' && showAffiliateStore) eventName = 'click_products_tab';
    
    if (eventName && itemKey && itemName && dataKey) {
      logAnalyticsEvent(eventName, {
        item_id: itemKey,
        item_name: itemName,
        item_category: dataKey
      });
    }

    // Navigate after logging
    if (newValue === 0) {
      navigate(`/${dataKey}/${itemKey}`); // Use itemKey to ensure correct navigation
    } else if (newValue === 1) {
      navigate(`/${dataKey}/${itemKey}/research`); // Use itemKey to ensure correct navigation
    }
  };

  const createLink = (name, key, type) => `<a href="/${type}/${key}">${name}</a>`;

  const addLinksToContent = (content) => {
    if (typeof content !== 'string') {
      return content; // If it's not a string, just return the content as it is.
    }

    let sanitizedContent = content;
    linkedItems.current.clear();

    supplements.forEach(supplement => {
      if (supplement.key !== itemKey && !linkedItems.current.has(supplement.key)) {
        const regex = new RegExp(`\\b${supplement.name}\\b`, 'i');
        sanitizedContent = sanitizedContent.replace(regex, match => createLink(match, supplement.key, 'supplements'));
        linkedItems.current.add(supplement.key);
      }
    });

    conditions.forEach(condition => {
      if (condition.key !== itemKey && !linkedItems.current.has(condition.key)) {
        const regex = new RegExp(`\\b${condition.name}\\b`, 'i');
        sanitizedContent = sanitizedContent.replace(regex, match => createLink(match, condition.key, 'conditions'));
        linkedItems.current.add(condition.key);
      }
    });

    return sanitizedContent;
  };

  const renderContentWithLinks = (content) => {
    if (Array.isArray(content)) {
      return (
        <ul>
          {content.map((item, index) => (
            <li key={index} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(addLinksToContent(item)) }} />
          ))}
        </ul>
      );
    }
    return <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(addLinksToContent(content)) }} />;
  };

  const CARD_CONFIG = {
    'benefits':                  { Icon: CheckCircleIcon,    label: 'Benefits' },
    'dosage':                    { Icon: LocalPharmacyIcon,  label: 'Dosage' },
    'possible side effects':     { Icon: WarningAmberIcon,   label: 'Possible Side Effects' },
    'interactions':              { Icon: BalanceIcon,        label: 'Interactions' },
    'how to take':               { Icon: AccessTimeIcon,     label: 'How to Take' },
    'food sources':              { Icon: LocalFloristIcon,   label: 'Food Sources' },
    'supplements that can help': { Icon: HealingIcon,        label: 'Supplements That May Help' },
    'safety':                    { Icon: ShieldIcon,         label: 'Safety' },
    'overview':                  { Icon: HealthAndSafetyIcon, label: 'Overview' },
  };

  function CardHeader({ card, detailKey }) {
    const rawTitle = card[detailKey] || '';
    const cfg = CARD_CONFIG[rawTitle.toLowerCase()] || { Icon: null, label: rawTitle };
    const { Icon, label } = cfg;
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        {Icon && (
          <Box sx={{
            width: 28, height: 28,
            borderRadius: '0.5rem',
            bgcolor: alpha(theme.palette.primary.light, 0.08),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon sx={{ fontSize: '0.95rem', color: theme.palette.primary.light }} />
          </Box>
        )}
        <Typography variant="overline" sx={{ color: theme.palette.primary.light, fontWeight: 700, lineHeight: 1 }}>
          {label}
        </Typography>
      </Stack>
    );
  }

  const getCardByTitle = (title) => selectedItem?.cards?.find(
    card => card[detailKey]?.toLowerCase() === title.toLowerCase()
  );

  const overviewText = getPlainText(getCardByTitle('Overview')?.content) || `${itemName} is a supplement in the myStack library.`;
  const dosageText = getFirstSentence(getCardByTitle('Dosage')?.content, 'Review dosage details below.');
  const interactionsText = getFirstSentence(getCardByTitle('Interactions')?.content, 'Check potential interactions before use.');

  useEffect(() => {
    const loadRelatedSupplements = async () => {
      if (!itemKey || supplements.length === 0) return;

      if (dataKey === 'supplements') {
        try {
          const response = await fetch(`${config.BASE_URL}/supplement-cosupplements/${encodeURIComponent(itemKey)}`);
          if (response.ok) {
            const data = await response.json();
            const ids = (data.supplements || []).filter(Boolean);
            if (ids.length > 0) {
              const supplementsByNormalizedKey = new Map(
                supplements.map(s => [normalizeSupplementKey(s.key), s])
              );
              const matched = ids
                .map(id => supplementsByNormalizedKey.get(normalizeSupplementKey(id)))
                .filter(Boolean)
                .slice(0, 5);
              setRelatedSupplements(matched);
              return;
            }
          }
        } catch (_) { /* fall through to text fallback */ }
      }

      // Text-extraction fallback: used for conditions and as supplement API fallback
      if (selectedItem?.cards) {
        const combinedContent = selectedItem.cards
          .map(card => getPlainText(card.content)).join(' ').toLowerCase();
        const matched = supplements.filter(s => {
          if (dataKey === 'supplements' && s.key === itemKey) return false;
          const escaped = s.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return new RegExp(`\\b${escaped}\\b`).test(combinedContent);
        }).slice(0, 5);
        setRelatedSupplements(matched);
      }
    };
    loadRelatedSupplements();
  }, [itemKey, dataKey, supplements, selectedItem]);

  const handleGoalClick = (goalSlug) => {
    const goalName = formatGoalLabel(goalSlug);
    logAnalyticsEvent('supplement_detail_chip_clicked', {
      supplement_key: itemKey,
      goal_slug: goalSlug,
    });
    navigate(`/create-stack?healthGoals=${encodeURIComponent(goalName)}&auto_generate=true`);
  };

  const [freeformGoal, setFreeformGoal] = useState('');
  const [ctaError, setCtaError] = useState(false);

  const submitFreeformGoal = (event) => {
    if (event) event.preventDefault();
    const goal = freeformGoal.trim();
    if (!goal) {
      logAnalyticsEvent('supplement_detail_freeform_empty_submitted', {
        supplement_key: itemKey,
        has_goal_chips: stackGoalSlugs.length > 0,
      });
      setCtaError(true);
      return;
    }
    setCtaError(false);
    logAnalyticsEvent('supplement_detail_freeform_submitted', {
      supplement_key: itemKey,
      goal_input: goal,
    });
    navigate(`/create-stack?healthGoals=${encodeURIComponent(goal)}&auto_generate=true`);
  };

  const SupplementLibraryLink = ({ compact = false }) => (
    <Button
      variant="text"
      size="small"
      startIcon={<KeyboardArrowLeftIcon />}
      onClick={() => navigate('/supplements')}
      sx={{
        mb: compact ? 1.5 : 1.25,
        ml: -0.75,
        px: 0.75,
        minWidth: 0,
        alignSelf: 'flex-start',
        color: alpha(theme.palette.text.primary, 0.62),
        fontWeight: 700,
        fontSize: '0.8125rem',
        textTransform: 'none',
        '& .MuiButton-startIcon': {
          mr: 0.35,
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.light, 0.08),
          color: theme.palette.primary.dark,
        },
      }}
    >
      Supplement Library
    </Button>
  );

  const ConditionLibraryLink = ({ compact = false }) => (
    <Button
      variant="text"
      size="small"
      startIcon={<KeyboardArrowLeftIcon />}
      onClick={() => navigate('/conditions')}
      sx={{
        mb: compact ? 1.5 : 1.25,
        ml: -0.75,
        px: 0.75,
        minWidth: 0,
        alignSelf: 'flex-start',
        color: alpha(theme.palette.text.primary, 0.62),
        fontWeight: 700,
        fontSize: '0.8125rem',
        textTransform: 'none',
        '& .MuiButton-startIcon': {
          mr: 0.35,
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.light, 0.08),
          color: theme.palette.primary.dark,
        },
      }}
    >
      Health Conditions Library
    </Button>
  );

  const renderSupplementOverview = () => {
    const hasStudies = researchTotal != null && researchTotal > 0;
    const studyCountLabel = `${researchTotal} studies`;

    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper elevation={0} sx={{
          p: { xs: 2.25, sm: 3 },
          borderRadius: '1.25rem',
          border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.12)}`,
          background: `linear-gradient(135deg, ${theme.surfaces?.brandTintSm ?? alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 60%)`,
          boxShadow: `0 0.75rem 2rem ${alpha(theme.palette.primary.light, 0.08)}`,
        }}>
          <SupplementLibraryLink />
          {(hasStudies || (showAffiliateStore && productCount > 0)) && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              {hasStudies && (
                <Chip
                  size="small"
                  icon={<ScienceIcon />}
                  label={studyCountLabel}
                  clickable
                  onClick={() => {
                    logAnalyticsEvent('supplement_detail_stat_chip_clicked', {
                      supplement_key: itemKey,
                      stat_type: 'studies',
                      stat_count: researchTotal,
                    });
                    document.getElementById('research-preview-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.light, 0.12) } }}
                />
              )}
              {showAffiliateStore && productCount > 0 && (
                <Chip
                  size="small"
                  icon={<ShoppingCartIcon />}
                  label={`${productCount} products`}
                  clickable
                  onClick={() => {
                    logAnalyticsEvent('supplement_detail_stat_chip_clicked', {
                      supplement_key: itemKey,
                      stat_type: 'products',
                      stat_count: productCount,
                    });
                    document.getElementById('products-preview-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.light, 0.12) } }}
                />
              )}
            </Stack>
          )}
          <Typography variant="h3" component="h1" sx={{
            fontSize: { xs: '2rem', sm: '2.5rem' },
            fontWeight: 700,
            color: theme.palette.text.primary,
            letterSpacing: '-0.03em',
            mb: 1,
          }}>
            {itemName}
          </Typography>
          <Typography variant="body1" sx={{
            color: alpha(theme.palette.text.primary, 0.76),
            lineHeight: 1.7,
            maxWidth: '46rem',
          }}>
            {overviewText}
          </Typography>
        </Paper>

        {StackCtaCard()}

        <DetailCardsSection />
        <RelatedSupplementsSection />
        <ResearchPreviewSection />
        <ProductsPreviewSection />
      </Box>
    );
  };

  const StackCtaCard = () => {
    const hasChips = stackGoalSlugs.length > 0;

    return (
      <Paper elevation={0} sx={{
        borderRadius: '1.25rem',
        border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.22)}`,
        boxShadow: `0 0.25rem 1rem ${alpha(theme.palette.primary.light, 0.1)}`,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          px: { xs: 2.25, sm: 2.75 }, pt: { xs: 2, sm: 2.25 }, pb: 1.75,
          borderBottom: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.1)}`,
        }}>
        <Box sx={{ mb: 0.4 }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            letterSpacing: '0.00625rem',
            // Was a sage-to-olive gradient. Neither of its colours is in the
            // brief, and brief §10 names sage/olive as the category default
            // the brand is defined against. Solid evergreen instead.
            color: color.evergreen,
          }}>myStack</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Start with a health goal to generate a personalized stack.
        </Typography>
        </Box>

        {/* Body */}
        <Box sx={{
          px: { xs: 2.25, sm: 2.75 },
          pt: { xs: 1.75, sm: 2 },
          pb: { xs: 2, sm: 2.25 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 1.75 },
        }}>
          {hasChips && (
            <Box>
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{
                  color: alpha(theme.palette.text.primary, 0.82),
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                }}>
                  Create a stack for
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 0.75, sm: 0.875 },
              }}>
                {stackGoalSlugs.map(goalSlug => (
                  <Chip
                    key={goalSlug}
                    label={(
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
                        <Box component="span">{formatGoalLabel(goalSlug)}</Box>
                        <ChevronRightIcon sx={{ fontSize: '1rem', mr: -0.35 }} />
                      </Box>
                    )}
                    clickable
                    size="small"
                    onClick={() => handleGoalClick(goalSlug)}
                    aria-label={`Create a stack for ${formatGoalLabel(goalSlug)}`}
                    sx={{
                      height: '2.25rem',
                      px: 0.25,
                      borderRadius: '999rem',
                      bgcolor: alpha(theme.palette.primary.light, 0.08),
                      color: theme.palette.primary.dark,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.24)}`,
                      boxShadow: `0 0.125rem 0.5rem ${alpha(theme.palette.primary.light, 0.06)}`,
                      transition: 'all 0.16s ease',
                      '& .MuiChip-label': {
                        px: 1.2,
                      },
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.light, 0.14),
                        borderColor: alpha(theme.palette.primary.light, 0.38),
                        color: theme.palette.primary.light,
                        transform: 'translateY(-0.0625rem)',
                        boxShadow: `0 0.375rem 1rem ${alpha(theme.palette.primary.light, 0.11)}`,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box
            component="form"
            onSubmit={submitFreeformGoal}
            sx={{
              width: '100%',
              maxWidth: { sm: '34rem' },
            }}
          >
            <Typography variant="caption" sx={{
              display: 'block',
              color: alpha(theme.palette.text.primary, 0.58),
              mb: 0.75,
              fontWeight: 700,
              letterSpacing: '0.01em',
            }}>
              {hasChips ? 'Or enter your own goal' : 'Enter a health goal'}
            </Typography>
            <Box sx={{
              display: 'block',
            }}>
              <TextField
                fullWidth
                placeholder={hasChips ? 'e.g. Better sleep, Stress relief' : 'Enter a health goal'}
                value={freeformGoal}
                onChange={(event) => { setFreeformGoal(event.target.value); if (ctaError) setCtaError(false); }}
                variant="outlined"
                size="small"
                error={ctaError}
                helperText={ctaError ? 'Please enter a health goal first' : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="submit"
                        aria-label="Create My Stack"
                        edge="end"
                        sx={{
                          width: '2rem',
                          height: '2rem',
                          mr: -0.35,
                          color: color.paper,
                          bgcolor: theme.palette.primary.main,
                          boxShadow: `0 0.125rem 0.5rem ${alpha(theme.palette.primary.light, 0.16)}`,
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                            boxShadow: `0 0.25rem 0.75rem ${alpha(theme.palette.primary.light, 0.2)}`,
                          },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: '1.15rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: color.paper,
                    borderRadius: '0.75rem',
                    boxShadow: `0 0.0625rem 0.125rem ${alpha(theme.palette.common.black, 0.04)}`,
                    '& fieldset': { borderColor: color.line },
                    '&:hover fieldset': { borderColor: alpha(theme.palette.primary.light, 0.4) },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                    '& input': {
                      py: 1.15,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const ConditionCtaCard = () => {
    const handleConditionChipClick = () => {
      logAnalyticsEvent('condition_detail_chip_clicked', {
        condition_key: itemKey,
        condition_name: itemName,
      });
      navigate(`/create-stack?healthGoals=${encodeURIComponent(itemName)}&auto_generate=true`);
    };

    const submitConditionFreeformGoal = (event) => {
      if (event) event.preventDefault();
      const goal = freeformGoal.trim();
      if (!goal) {
        logAnalyticsEvent('condition_detail_freeform_empty_submitted', { condition_key: itemKey });
        setCtaError(true);
        return;
      }
      setCtaError(false);
      logAnalyticsEvent('condition_detail_freeform_submitted', {
        condition_key: itemKey,
        goal_input: goal,
      });
      navigate(`/create-stack?healthGoals=${encodeURIComponent(goal)}&auto_generate=true`);
    };

    return (
      <Paper elevation={0} sx={{
        borderRadius: '1.25rem',
        border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.22)}`,
        boxShadow: `0 0.25rem 1rem ${alpha(theme.palette.primary.light, 0.1)}`,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          px: { xs: 2.25, sm: 2.75 }, pt: { xs: 2, sm: 2.25 }, pb: 1.75,
          borderBottom: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.1)}`,
        }}>
          <Box sx={{ mb: 0.4 }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              letterSpacing: '0.00625rem',
              // See the note on the sibling heading above — solid evergreen,
              // not the off-palette sage/olive gradient.
              color: color.evergreen,
            }}>myStack</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Start with a health goal to generate a personalized stack.
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{
          px: { xs: 2.25, sm: 2.75 },
          pt: { xs: 1.75, sm: 2 },
          pb: { xs: 2, sm: 2.25 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 1.75 },
        }}>
          {/* Condition chip */}
          <Box>
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="body2" sx={{
                color: alpha(theme.palette.text.primary, 0.82),
                fontWeight: 700,
                letterSpacing: '-0.01em',
              }}>
                Create a Support Stack for:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 0.875 } }}>
              <Chip
                label={(
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
                    <Box component="span">{itemName}</Box>
                    <ChevronRightIcon sx={{ fontSize: '1rem', mr: -0.35 }} />
                  </Box>
                )}
                clickable
                size="small"
                onClick={handleConditionChipClick}
                aria-label={`Create a support stack for ${itemName}`}
                sx={{
                  height: '2.25rem',
                  px: 0.25,
                  borderRadius: '999rem',
                  bgcolor: alpha(theme.palette.primary.light, 0.08),
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.24)}`,
                  boxShadow: `0 0.125rem 0.5rem ${alpha(theme.palette.primary.light, 0.06)}`,
                  transition: 'all 0.16s ease',
                  '& .MuiChip-label': { px: 1.2 },
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.light, 0.14),
                    borderColor: alpha(theme.palette.primary.light, 0.38),
                    color: theme.palette.primary.light,
                    transform: 'translateY(-0.0625rem)',
                    boxShadow: `0 0.375rem 1rem ${alpha(theme.palette.primary.light, 0.11)}`,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Freeform goal */}
          <Box
            component="form"
            onSubmit={submitConditionFreeformGoal}
            sx={{ width: '100%', maxWidth: { sm: '34rem' } }}
          >
            <Typography variant="caption" sx={{
              display: 'block',
              color: alpha(theme.palette.text.primary, 0.58),
              mb: 0.75,
              fontWeight: 700,
              letterSpacing: '0.01em',
            }}>
              Or enter your own goal
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Better sleep, Stress relief"
              value={freeformGoal}
              onChange={(event) => { setFreeformGoal(event.target.value); if (ctaError) setCtaError(false); }}
              variant="outlined"
              size="small"
              error={ctaError}
              helperText={ctaError ? 'Please enter a health goal first' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="submit"
                      aria-label="Create My Stack"
                      edge="end"
                      sx={{
                        width: '2rem',
                        height: '2rem',
                        mr: -0.35,
                        color: color.paper,
                        bgcolor: theme.palette.primary.main,
                        boxShadow: `0 0.125rem 0.5rem ${alpha(theme.palette.primary.light, 0.16)}`,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                          boxShadow: `0 0.25rem 0.75rem ${alpha(theme.palette.primary.light, 0.2)}`,
                        },
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: '1.15rem' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: color.paper,
                  borderRadius: '0.75rem',
                  boxShadow: `0 0.0625rem 0.125rem ${alpha(theme.palette.common.black, 0.04)}`,
                  '& fieldset': { borderColor: color.line },
                  '&:hover fieldset': { borderColor: alpha(theme.palette.primary.light, 0.4) },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.light },
                  '& input': { py: 1.15 },
                },
              }}
            />
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderConditionOverview = () => {
    const conditionOverviewText = getPlainText(getCardByTitle('Overview')?.content) || `${itemName} is a health condition in the myStack library.`;

    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper elevation={0} sx={{
          p: { xs: 2.25, sm: 3 },
          borderRadius: '1.25rem',
          border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.12)}`,
          background: `linear-gradient(135deg, ${theme.surfaces?.brandTintSm ?? alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 60%)`,
          boxShadow: `0 0.75rem 2rem ${alpha(theme.palette.primary.light, 0.08)}`,
        }}>
          <ConditionLibraryLink />
          <Typography variant="h3" component="h1" sx={{
            fontSize: { xs: '2rem', sm: '2.5rem' },
            fontWeight: 700,
            color: theme.palette.text.primary,
            letterSpacing: '-0.03em',
            mb: 1,
          }}>
            {itemName}
          </Typography>
          <Typography variant="body1" sx={{
            color: alpha(theme.palette.text.primary, 0.76),
            lineHeight: 1.7,
            maxWidth: '46rem',
          }}>
            {conditionOverviewText}
          </Typography>
        </Paper>

        {ConditionCtaCard()}

        <DetailCardsSection />
        <RelatedSupplementsSection />
      </Box>
    );
  };

  const ResearchPreviewSection = () => {
    if (researchPreviewLoading || researchPreview.length === 0) return null;

    return (
      <Paper id="research-preview-section" elevation={0} sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: '1rem',
      border: `0.0625rem solid ${alpha(theme.palette.divider, 0.8)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
        <Box>
          <Typography variant="overline" sx={{ color: theme.palette.primary.light, fontWeight: 700 }}>
            Research Snapshot
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Studies on {itemName}</Typography>
        </Box>
        <Button
          variant="text"
          onClick={() => {
            logAnalyticsEvent('supplement_detail_research_view_all_clicked', {
              supplement_key: itemKey,
              preview_count: researchPreview.length,
              total_count: researchTotal,
            });
            navigate(`/supplements/${itemKey}/research`);
          }}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          View all
        </Button>
      </Box>
        <Stack spacing={1.5}>
          {researchPreview.map(article => {
            const pubDate = article.date_published ? new Date(article.date_published) : null;
            const monthYear = pubDate ? pubDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null;
            const studyType = article.ai_study_type || null;
            const subjects = article.ai_number_of_subjects ?? null;
            return (
              <Paper
                key={article.article_ref}
                elevation={0}
                onClick={() => {
                  logAnalyticsEvent('supplement_detail_research_preview_clicked', {
                    supplement_key: itemKey,
                    article_ref: article.article_ref,
                  });
                  navigate(`/supplements/${itemKey}/research/${article.article_ref}`);
                }}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '0.875rem',
                  border: `0.0625rem solid ${alpha(theme.palette.primary.light, 0.12)}`,
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.light, 0.28),
                    transform: 'translateY(-0.0625rem)',
                    boxShadow: `0 0.375rem 1rem ${alpha(theme.palette.primary.light, 0.1)}`,
                  },
                }}
              >
                {/* Row 1: journal name (left) + year (right) */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                  <Typography sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    fontStyle: 'italic',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}>
                    {article.journal_title
                      ? article.journal_title.replace(/\b\w/g, c => c.toUpperCase())
                      : ''}
                  </Typography>
                  {monthYear && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                      <CalendarTodayIcon sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', lineHeight: 1 }}>{monthYear}</Typography>
                    </Box>
                  )}
                </Box>

                {/* Row 2: title */}
                <Typography sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  lineHeight: 1.5,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                  mb: 0.875,
                }}>
                  {article.ai_title_simplified || article.title}
                </Typography>

                {/* Row 3: strength + study type chip (same line) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <StudyStrengthIndicator score={article.ai_study_strength?.score || 0} source={article.ai_study_strength?.source} />
                  {studyType && (
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.45,
                      px: 0.875, py: 0.3,
                      borderRadius: '0.375rem',
                      bgcolor: color.surface,
                    }}>
                      <BiotechIcon sx={{ fontSize: '0.65rem', color: color.inkSoft, opacity: 0.85 }} />
                      <Typography sx={{
                        fontSize: '0.65rem', fontWeight: 700, color: color.inkSoft,
                        letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1,
                      }}>
                        {studyType}
                      </Typography>
                    </Box>
                  )}
                  {subjects !== null && (
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.45,
                      px: 0.875, py: 0.3,
                      borderRadius: '0.375rem',
                      bgcolor: color.surface,
                    }}>
                      <PeopleAltIcon sx={{ fontSize: '0.65rem', color: color.inkSoft, opacity: 0.85 }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: color.inkSoft, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1 }}>
                        n={subjects.toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
    </Paper>
    );
  };

  const DetailCardsSection = () => (
    <Box className="detail-content" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {selectedItem.cards.filter(card => card[detailKey]?.toLowerCase() !== 'overview').map((card, index) => (
        <Paper
          key={`${detailKey}-${index}`}
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.75 },
            pl: { xs: 2.25, sm: 3 },
            borderRadius: '1rem',
            border: `0.0625rem solid ${alpha(theme.palette.divider, 0.6)}`,
            borderLeft: `0.1875rem solid ${alpha(theme.palette.primary.light, 0.35)}`,
            bgcolor: theme.palette.background.paper,
            transition: 'border-left-color 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              borderLeftColor: alpha(theme.palette.primary.light, 0.75),
              boxShadow: `0 0.375rem 1.25rem ${alpha(theme.palette.primary.light, 0.07)}`,
            },
          }}
        >
          <CardHeader card={card} detailKey={detailKey} />
          <Typography
            component="div"
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.925rem', '& ul': { pl: 0, listStyle: 'none', m: 0 }, '& strong': { fontWeight: 600 }, '& a': { fontSize: 'inherit' } }}
          >
            {renderContentWithLinks(card.content)}
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  const RelatedSupplementsSection = () => {
    if (relatedSupplements.length === 0) return null;
    return (
      <Paper elevation={0} sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '1rem',
        border: `0.0625rem solid ${alpha(theme.palette.divider, 0.8)}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Box sx={{ mb: 1.75 }}>
          <Typography variant="overline" sx={{ color: theme.palette.primary.light, fontWeight: 700 }}>
            Related Supplements
          </Typography>
        </Box>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {relatedSupplements.map(supplement => (
            <Chip
              key={supplement.key}
              clickable
              icon={<HealingIcon />}
              label={supplement.name}
              onClick={() => {
                logAnalyticsEvent('supplement_detail_related_supplement_clicked', {
                  supplement_key: itemKey,
                  related_supplement_key: supplement.key,
                  related_supplement_name: supplement.name,
                });
                navigate(`/supplements/${supplement.key}`);
              }}
              sx={{
                height: '2.125rem',
                borderRadius: '999rem',
                border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                fontWeight: 700,
                fontSize: '0.8rem',
                transition: 'all 0.16s ease',
                boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.018)',
                '& .MuiChip-icon': {
                  color: theme.palette.primary.light,
                  fontSize: '0.95rem',
                  ml: 0.75,
                  mr: -0.25,
                },
                '& .MuiChip-label': {
                  px: 1.1,
                  letterSpacing: '-0.005em',
                },
                '&:hover': {
                  bgcolor: theme.surfaces.brandTintSm,
                  borderColor: alpha(theme.palette.primary.light, 0.28),
                  transform: 'translateY(-0.0625rem)',
                  boxShadow: `0 0.375rem 1rem ${alpha(theme.palette.primary.light, 0.08)}`,
                },
              }}
            />
          ))}
        </Stack>
      </Paper>
    );
  };

  const ProductsPreviewSection = () => {
    if (!showAffiliateStore || productPreview.length === 0) return null;
    return (
      <Paper id="products-preview-section" elevation={0} sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '1rem',
        border: `0.0625rem solid ${alpha(theme.palette.divider, 0.8)}`,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box>
            <Typography variant="overline" sx={{ color: theme.palette.primary.light, fontWeight: 700 }}>
              Products
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Shop {itemName}</Typography>
          </Box>
          <Button
            variant="text"
            onClick={() => {
              logAnalyticsEvent('supplement_detail_products_see_all_clicked', {
                supplement_key: itemKey,
                preview_count: productPreview.length,
                total_count: productCount,
              });
              navigate(`/market/${itemKey}`);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            See all
          </Button>
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5,
        }}>
          {productPreview.map(product => (
            <ProductCard
              key={product.id || product.asin}
              product={{ ...product, supplementKey: itemKey }}
              compact
              linkToProductPage
            />
          ))}
        </Box>
      </Paper>
    );
  };

  // Don't render anything until the essential item data is loaded
  if (!selectedItem || !itemName) {
    // You might want a loading indicator here
    return null;
  }

  // Condition detail pages use the supplement-style overview layout (no tabs).
  if (dataKey === 'conditions') {
    return (
      <Box sx={{ width: '100%', padding: { xs: 1, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Helmet>
          <title>{`${itemName} - Symptoms & Treatments | myStack`}</title>
          <meta name="description" content={seoDesc} />
          {!location.pathname.includes('/research') && (
            <link rel="canonical" href={`https://my-stack.ai/conditions/${encodePathSegment(itemKey)}`} />
          )}
          {structuredData && (
            <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
          )}
        </Helmet>
        <Box sx={{ width: '100%', maxWidth: '62.5rem' }} ref={readContentRef}>
          {renderConditionOverview()}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', padding: { xs: 1, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Helmet>
        <title>{`${itemName} - Benefits, Dosage & Research | myStack`}</title>
        <meta name="description" content={seoDesc} />
        {!location.pathname.includes('/research') && (
          <link rel="canonical" href={`https://my-stack.ai/supplements/${encodePathSegment(itemKey)}`} />
        )}
        {structuredData && (
          <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        )}
      </Helmet>

      <Paper elevation={2} sx={{ width: '100%', maxWidth: '62.5rem', mb: 3, overflow: 'hidden', borderRadius: '0.75rem' }}>
        <Box sx={{
          width: '100%',
          maxWidth: '75rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 4
        }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Details" />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Research</span>
              </Box>
            } />
          </Tabs>
        </Box>

        {/* Content section - centered with max-width */}
        <Box sx={{
          width: '100%',
          maxWidth: '75rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {activeTab === 0 && selectedItem && (
            <Box className="detail-content" ref={readContentRef} sx={{
              width: '100%',
              maxWidth: '62.5rem'
            }}>
              {renderSupplementOverview()}
            </Box>
          )}

          {activeTab === 1 && !articleId && (
            <Box sx={{
              width: '100%',
              maxWidth: '62.5rem',
              px: { xs: 0, sm: '1rem' }
            }}>
              <SupplementLibraryLink compact />
              <Research
                itemName={itemName}
                conditions={conditions}
                supplements={supplements}
              />
            </Box>
          )}

          {activeTab === 1 && articleId && (
            <Box sx={{
              width: '100%',
              maxWidth: '62.5rem',
              px: { xs: 0, sm: '1rem' }
            }}>
              <SupplementLibraryLink compact />
              <ArticleDetail
                conditions={conditions}
                supplements={supplements}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default Detail;
